import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  installSoftware,
  uninstallSoftware,
  startSoftware,
  stopSoftware,
  setExpertSkill,
  setTranslatorLanguage,
  setDatabaseTopic,
  setVirtualCrewRoles,
  setVirtualGunnerRoles,
} from "../../../../store/actions/shipBuilderActions";
import {
  calculateSoftwareStats,
  getSoftwareCategories,
  getSoftwareByCategory,
  validateSoftwareSelection,
} from "../../../../utils/calculations/softwareCalculations";
import { SoftwareSelection } from "../../../../types/software";
import { formatCurrency } from "../../../../utils/formatting";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";

export const SoftwareSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const software: SoftwareSelection = useAppSelector(
    (state) => state.shipBuilder.software,
  );
  const computer = useAppSelector((state) => state.shipBuilder.computer);
  const shipTechLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);

  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(
    new Set(),
  );

  const isIncludedSoftware = (packageId: string): boolean => {
    // These software packages come included with every ship
    const includedPackages = ["manoeuvre", "intellect", "library"];
    return includedPackages.includes(packageId);
  };

  const categories = ["All", ...getSoftwareCategories()];
  const filteredPackages =
    selectedCategory === "All"
      ? getSoftwareByCategory("") // This will return all packages
      : getSoftwareByCategory(selectedCategory);

  // Sort packages with included software first, then alphabetically
  const sortedPackages = [...filteredPackages].sort((a, b) => {
    const aIsIncluded = isIncludedSoftware(a.id);
    const bIsIncluded = isIncludedSoftware(b.id);
    
    // If one is included and the other isn't, included comes first
    if (aIsIncluded && !bIsIncluded) return -1;
    if (!aIsIncluded && bIsIncluded) return 1;
    
    // If both are included or both are not included, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  const stats = calculateSoftwareStats(software || { installed: [], running: [] }, computer);
  const validation = validateSoftwareSelection(
    software || { installed: [], running: [] },
    computer,
    shipTechLevel,
    tlOverrideActive
  );

  const handleInstallSoftware = (packageId: string, level: number) => {
    dispatch(installSoftware({ packageId, level }));
  };

  const handleUninstallSoftware = (packageId: string, level: number) => {
    dispatch(uninstallSoftware({ packageId, level }));
  };

  const handleStartSoftware = (packageId: string, level: number) => {
    dispatch(startSoftware({ packageId, level }));
  };

  const handleStopSoftware = (packageId: string, level: number) => {
    dispatch(stopSoftware({ packageId, level }));
  };

  const handleSetExpertSkill = (instanceId: string, skill: string) => {
    dispatch(setExpertSkill({ instanceId, skill }));
  };

  const handleSetTranslatorLanguage = (instanceId: string, language: string) => {
    dispatch(setTranslatorLanguage({ instanceId, language }));
  };

  const handleSetDatabaseTopic = (instanceId: string, topic: string) => {
    dispatch(setDatabaseTopic({ instanceId, topic }));
  };

  const handleSetVirtualCrewRoles = (instanceId: string, roles: string[]) => {
    dispatch(setVirtualCrewRoles({ instanceId, roles }));
  };

  const handleSetVirtualGunnerRoles = (instanceId: string, roles: string[]) => {
    dispatch(setVirtualGunnerRoles({ instanceId, roles }));
  };

  // const handleLevelChange = (packageId: string, level: number) => {
  //   dispatch(setSoftwareLevel({ packageId, level }));
  // };

  const togglePackageExpansion = (packageId: string) => {
    const newExpanded = new Set(expandedPackages);
    if (newExpanded.has(packageId)) {
      newExpanded.delete(packageId);
    } else {
      newExpanded.add(packageId);
    }
    setExpandedPackages(newExpanded);
  };

  const isSoftwareInstalled = (packageId: string, level: number): boolean => {
    return (software?.installed || []).some(
      (s) => s.packageId === packageId && s.level === level && s.installed,
    );
  };

  const isSoftwareRunning = (packageId: string, level: number): boolean => {
    return (software?.running || []).some(
      (s) => s.packageId === packageId && s.level === level && s.running,
    );
  };

  const getInstalledInstances = (packageId: string, level: number) => {
    return (software?.installed || []).filter(
      (s) => s.packageId === packageId && s.level === level && s.installed,
    );
  };

  // const getInstalledLevel = (packageId: string): number => {
  //   const installed = software.installed.find(s => s.packageId === packageId && s.installed);
  //   return installed?.level || 0;
  // };

  const canRunSoftware = (packageId: string, level: number): boolean => {
    return isSoftwareInstalled(packageId, level) && validation.isValid;
  };

  const getBandwidthColor = (used: number, available: number): string => {
    const percentage = (used / available) * 100;
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 75) return "text-yellow-600";
    return "text-green-600";
  };

  const isLockedOut = (pkg: any, level?: number): boolean => {
    if (level !== undefined && pkg.levels) {
      const lvl = pkg.levels.find((l: any) => l.level === level);
      return lvl ? shipTechLevel < lvl.tl : false;
    }
    return shipTechLevel < pkg.tl;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Install Software
        </h2>

        {/* Software Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Bandwidth Usage
            </h3>
            <p
              className={`text-2xl font-bold ${getBandwidthColor(stats.totalBandwidth, stats.availableBandwidth)}`}
            >
              {stats.totalBandwidth} / {stats.availableBandwidth}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
              Installed Packages
            </h3>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.installedPackages}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-800 dark:text-purple-200">
              Running Packages
            </h3>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {stats.runningPackages}
            </p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/30 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">Total Cost</h3>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {formatCurrency(stats.totalCost * 1000000)}
            </p>
          </div>
        </div>

        {/* Validation Errors */}
        {!validation.isValid && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md p-4 mb-6">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
              Validation Errors:
            </h3>
            <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
              {validation.errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Filter by Category
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {/* Software Packages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedPackages.map((pkg) => (
            <div key={pkg.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm p-4 mb-2 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {pkg.name}
                    </h3>
                    {isIncludedSoftware(pkg.id) && (
                      <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-full">
                        Included
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{pkg.category}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">TL {pkg.tl}</span>
                  <button
                    onClick={() => togglePackageExpansion(pkg.id)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    {expandedPackages.has(pkg.id) ? "▼" : "▶"}
                  </button>
                </div>
              </div>

              {expandedPackages.has(pkg.id) && (
                <div className="mt-4 space-y-3">
                  {pkg.levels ? (
                    // Multi-level software
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Select Level:
                      </h4>
                      {pkg.levels.map((level) => {
                        const isInstalled = isSoftwareInstalled(
                          pkg.id,
                          level.level,
                        );
                        const isRunning = isSoftwareRunning(
                          pkg.id,
                          level.level,
                        );
                        const canRun = canRunSoftware(pkg.id, level.level);

                        return (
                          <div
                            key={level.level}
                            className="flex flex-col p-3 bg-gray-50 dark:bg-gray-600 rounded-md mb-2"
                          >
                            <div className="flex flex-row items-center space-x-4">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Level {level.level}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-300">
                                Bandwidth: {level.bandwidth}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-300">
                                Cost: {formatCurrency(level.cost * 1000000)}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-300">
                                TL: {level.tl}
                              </span>
                            </div>
                            {level.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {level.description}
                              </p>
                            )}
                            <div className="flex flex-row space-x-2 mt-2">
                              {!isInstalled ? (
                                <button
                                  onClick={() =>
                                    handleInstallSoftware(pkg.id, level.level)
                                  }
                                  className={`w-full px-3 py-1 text-sm rounded ${isLockedOut(pkg, level.level) ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                                  disabled={isLockedOut(pkg, level.level)}
                                  title={
                                    isLockedOut(pkg, level.level)
                                      ? `Requires TL ${level.tl}`
                                      : ""
                                  }
                                >
                                  Install
                                </button>
                              ) : (
                                <>
                                  {isIncludedSoftware(pkg.id) ? (
                                    <span className="w-full px-3 py-1 text-sm bg-green-100 text-green-800 rounded block text-center">
                                      Included
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        handleUninstallSoftware(
                                          pkg.id,
                                          level.level,
                                        )
                                      }
                                      className="w-full px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                                    >
                                      Uninstall
                                    </button>
                                  )}
                                  {canRun && !isRunning && (
                                    <button
                                      onClick={() =>
                                        handleStartSoftware(
                                          pkg.id,
                                          level.level,
                                        )
                                      }
                                      className="w-full px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                    >
                                      Start
                                    </button>
                                  )}
                                  {isRunning && (
                                    <button
                                      onClick={() =>
                                        handleStopSoftware(
                                          pkg.id,
                                          level.level,
                                        )
                                      }
                                      className="w-full px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                                    >
                                      Stop
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Single-level software
                    <div className="flex flex-col p-3 bg-gray-50 dark:bg-gray-600 rounded-md mb-2">
                      <div className="flex flex-row items-center space-x-4">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Basic</span>
                        <span className="text-sm text-gray-600 dark:text-gray-300 ml-4">
                          Bandwidth: {pkg.bandwidth}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-300 ml-4">
                          Cost: {formatCurrency(pkg.cost * 1000000)}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-300 ml-4">
                          TL: {pkg.tl}
                        </span>
                      </div>
                      <div className="flex flex-row space-x-2 mt-2">
                        {!isSoftwareInstalled(pkg.id, 0) ? (
                          <button
                            onClick={() => handleInstallSoftware(pkg.id, 0)}
                            className={`w-full px-3 py-1 text-sm rounded ${isLockedOut(pkg) ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                            disabled={isLockedOut(pkg)}
                            title={isLockedOut(pkg) ? `Requires TL ${pkg.tl}` : ""}
                          >
                            Install
                          </button>
                        ) : (
                          <>
                            {isIncludedSoftware(pkg.id) ? (
                              <span className="w-full px-3 py-1 text-sm bg-green-100 text-green-800 rounded block text-center">
                                Included
                              </span>
                            ) : (
                              <button
                                onClick={() => handleUninstallSoftware(pkg.id, 0)}
                                className="w-full px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                Uninstall
                              </button>
                            )}
                            {canRunSoftware(pkg.id, 0) && !isSoftwareRunning(pkg.id, 0) && (
                              <button
                                onClick={() => handleStartSoftware(pkg.id, 0)}
                                className="w-full px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
                              >
                                Start
                              </button>
                            )}
                            {isSoftwareRunning(pkg.id, 0) && (
                              <button
                                onClick={() => handleStopSoftware(pkg.id, 0)}
                                className="w-full px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                              >
                                Stop
                              </button>
                            )}
                          </>
                        )}

                        {/* Software Configuration for Each Instance */}
                        {isSoftwareInstalled(pkg.id, 0) && (
                          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded border dark:border-blue-700">
                            <div className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">Installed Instances:</div>
                            {getInstalledInstances(pkg.id, 0).map((instance, index) => (
                              <div key={instance.instanceId} className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border dark:border-blue-600">
                                <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                                  Instance {index + 1}
                                </div>
                                
                                {/* Expert Software Configuration */}
                                {pkg.id === "expert" && (
                                  <div className="space-y-2">
                                    <label className="text-xs font-medium text-blue-800 dark:text-blue-200">
                                      Skill:
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter skill (e.g., Pilot, Engineer, Gunner)"
                                      value={software?.expertSkills?.[instance.instanceId || ''] || ""}
                                      onChange={(e) => handleSetExpertSkill(instance.instanceId || '', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                                    />
                                  </div>
                                )}

                                {/* Translator Software Configuration */}
                                {pkg.id === "translator" && (
                                  <div className="space-y-2">
                                    <label className="text-xs font-medium text-blue-800 dark:text-blue-200">
                                      Language:
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter language (e.g., Vilani, Aslan, Vargr)"
                                      value={software?.translatorLanguages?.[instance.instanceId || ''] || ""}
                                      onChange={(e) => handleSetTranslatorLanguage(instance.instanceId || '', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                                    />
                                  </div>
                                )}

                                {/* Database Software Configuration */}
                                {pkg.id === "database" && (
                                  <div className="space-y-2">
                                    <label className="text-xs font-medium text-blue-800 dark:text-blue-200">
                                      Topic:
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter database topic (e.g., Starship Engineering, Alien Cultures)"
                                      value={software?.databaseTopics?.[instance.instanceId || ''] || ""}
                                      onChange={(e) => handleSetDatabaseTopic(instance.instanceId || '', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                                    />
                                  </div>
                                )}

                                {/* Virtual Crew Configuration */}
                                {pkg.id === "virtualCrew" && (
                                  <div className="space-y-2">
                                    <label className="text-xs font-medium text-blue-800">
                                      Crew Roles to Replace:
                                    </label>
                                    <div className="space-y-1">
                                      {["pilot", "sensorOperator"].map((role) => (
                                        <label key={role} className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={software?.virtualCrewRoles?.[instance.instanceId || '']?.includes(role) || false}
                                            onChange={(e) => {
                                              const currentRoles = software?.virtualCrewRoles?.[instance.instanceId || ''] || [];
                                              const newRoles = e.target.checked
                                                ? [...currentRoles, role]
                                                : currentRoles.filter(r => r !== role);
                                              handleSetVirtualCrewRoles(instance.instanceId || '', newRoles);
                                            }}
                                            className="mr-2"
                                          />
                                          <span className="text-xs text-blue-700 capitalize">
                                            {role.replace(/([A-Z])/g, ' $1').trim()}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Virtual Gunner Configuration */}
                                {pkg.id === "virtualGunner" && (
                                  <div className="space-y-2">
                                    <label className="text-xs font-medium text-blue-800">
                                      Gunner Roles to Replace:
                                    </label>
                                    <div className="space-y-1">
                                      {["turret", "barbette", "bay", "screen"].map((role) => (
                                        <label key={role} className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={software?.virtualGunnerRoles?.[instance.instanceId || '']?.includes(role) || false}
                                            onChange={(e) => {
                                              const currentRoles = software?.virtualGunnerRoles?.[instance.instanceId || ''] || [];
                                              const newRoles = e.target.checked
                                                ? [...currentRoles, role]
                                                : currentRoles.filter(r => r !== role);
                                              handleSetVirtualGunnerRoles(instance.instanceId || '', newRoles);
                                            }}
                                            className="mr-2"
                                          />
                                          <span className="text-xs text-blue-700 capitalize">
                                            {role}
                                          </span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Total Cost */}
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Software Cost Summary
          </h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Total Software Cost: {formatCurrency(stats.totalCost * 1000000)}
          </p>
        </div>
      </div>
    </div>
  );
};
